<script setup lang="ts">
import {
  trash,
  personCircleOutline,
  personAddSharp,
  close,
  save,
} from "ionicons/icons";
import {
  IonLabel,
  IonItem,
  IonIcon,
  IonAlert,
  IonButton,
  IonList,
  IonModal,
  IonInput,
  IonRow,
  IonGrid,
  IonCol,
  IonLoading,
  IonToggle,
} from "@ionic/vue";
import { useUserStore } from "@/stores/user";
import { useContextStore } from "@/stores/context";
import { usePlansStore } from "@/stores/plans";

import { onMounted, reactive } from "vue";
import {
  getEventManagers,
  addEventManager,
  deleteEventManager,
} from "@/api/events";
import { createUser, fetchUserByEmail } from "@/api/users";
import { getSubscribedPlans } from "@/api/plans";
import { IClientPaymentPlanParams } from "@/models/plan";
import { useToasters } from "@/composition/toasters";
import router from "@/router";
import { event as gtag_event } from "vue-gtag";

const toasters = useToasters();
const userStore = useUserStore();
const contextStore = useContextStore();
const plansStore = usePlansStore();

onMounted(() => {
  userStore.getUserPermissions();
  fetchEventManagers();
  fetchCurrentPlan();
});

const state = reactive({
  loading: false,
  managers: [],
  alertEventManager: false,
  currentEventMngrEdition: 0,
  currentClientAccountEdition: 0,
  modalForm: false,
  user_form: {
    id: 0,
    name: "",
    email: "",
    password: "",
    event: 0,
  },
  event_manager_form: {
    user_account: 0,
    event: 0,
    name: "",
  },
  userNeedToBeCreated: false,
  toggleNewUser: false,
  subscribed_plans: Array<IClientPaymentPlanParams>,
  current_plan: {},
});

const fetchEventManagers = () => {
  state.loading = true;
  getEventManagers(contextStore.event.id).then((response) => {
    state.managers = response.data.results;
    state.loading = false;
  });
};

const fetchCurrentPlan = async () => {
  await plansStore.fetch({ reset: true, page_size: 10 });
  // state.current_plan = plansStore.results[0];

  const plan = await getSubscribedPlans();
  state.subscribed_plans = plan.data.results;

  let current_plan_id = 0;

  state.subscribed_plans.forEach((element: any) => {
    if (element.subscription_status === "active") {
      current_plan_id = element.payment_plan;
      return;
    }
  });

  // Get plan information
  const current_plan = await plansStore.fetchPlan(current_plan_id);
  state.current_plan = current_plan.result;
};

const openDialogDelete = (
  event_manager_id: number,
  client_account_id: number
) => {
  state.alertEventManager = true;
  state.currentEventMngrEdition = event_manager_id;
  state.currentClientAccountEdition = client_account_id;
};

const deleteButtons = [
  {
    text: "Cancel",
    role: "cancel",
    cssClass: "alert-button-cancel",
    handler: () => {
      state.alertEventManager = false;
    },
  },
  {
    text: " OK ",
    role: "confirm",
    cssClass: "alert-button-confirm",
    handler: () => {
      state.alertEventManager = false;
      deleteEventManager(
        state.currentEventMngrEdition,
        contextStore.event.id
      ).then(() => {
        toasters.addSuccess("Event manager deleted successfully.");
        fetchEventManagers();
      });
    },
  },
];

const submit = () => {
  state.user_form.event = contextStore.event.id;
  state.event_manager_form.event = contextStore.event.id;
  state.event_manager_form.name = state.user_form.name;

  // Create a new beamian user if user doesn't exists
  if (state.user_form.email && state.userNeedToBeCreated) {
    createNewEventManagerUser();
  } else {
    // Proceed with the event manager invite
    addEventManager(state.event_manager_form).then(() => {
      state.loading = false;
      state.modalForm = false;
      router.push(`/event/${contextStore.event.id}/team-management`);
      toasters.addSuccess("Event manager invited succesfully.");
      gtag_event("event_manager_added", {
        category: "check-in app",
        label: "Event manager added",
        description: `${plansStore.currentPlan.name}`,
      });
      fetchEventManagers();

      if (
        state.managers.length ===
        plansStore.currentPlan.check_in_max_event_managers
      ) {
        gtag_event("limits_achieved", {
          category: "check-in app",
          label: "Limits achieved while trying to add a event manager",
          description: `${plansStore.currentPlan.name}`,
        });
      }
    });
  }
};

const createNewEventManagerUser = () => {
  if (
    state.user_form.email &&
    state.user_form.name &&
    state.user_form.password &&
    state.user_form.event
  ) {
    state.loading = true;
    createUser(state.user_form).then((response) => {
      state.event_manager_form.user_account = response.data.id;
      state.event_manager_form.name = response.data.name;

      // Proceed with the event manager invite
      addEventManager(state.event_manager_form)
        .then(() => {
          state.loading = false;
          state.modalForm = false;
          router.push(`/event/${contextStore.event.id}/team-management`);
          toasters.addSuccess("Event manager invited succesfully.");
          fetchEventManagers();
        })
        .catch(() => {
          toasters.addError("Failed to invite the user.");
        });
    });
  }
};

const searchForUserEmail = () => {
  const user_is_existing_manager = checkIfUserIsEventManager(
    state.user_form.email
  );

  if (user_is_existing_manager) {
    toasters.addWarning(`The user already is a event manager of this event.`);
  } else {
    state.loading = true;
    fetchUserByEmail(state.user_form.email).then((response) => {
      if (response.data.results.length) {
        const existing_user = response.data.results[0];
        state.event_manager_form.user_account = existing_user.id;
        submit();
      } else {
        state.userNeedToBeCreated = true;
      }
      state.loading = false;
    });
  }
};

const checkIfUserIsEventManager = (email: string) => {
  const manager_exists = state.managers.filter(
    (mngr: object) => mngr.email === email
  );
  return manager_exists.length ? true : false;
};
</script>
<template>
  <div>
    <ion-item class="header">
      <ion-label>
        <h1>Event Managers</h1>
        <p class="subheader">
          <b>{{ contextStore.event.name }}</b>
        </p>
        <p
          v-if="
            state.managers && plansStore.currentPlan.check_in_max_event_managers
          "
        >
          {{ state.managers.length }} of
          {{ plansStore.currentPlan.check_in_max_event_managers }} Event
          Managers
        </p>
      </ion-label>
      <ion-button
        fill="clear"
        :disabled="
          state.managers.length >=
          plansStore.currentPlan.check_in_max_event_managers
        "
        @click="state.modalForm = true"
        v-if="userStore.userIsAdmin"
      >
        <ion-icon
          :icon="personAddSharp"
          size="small"
          slot="end"
          color="primary"
        >
        </ion-icon>
      </ion-button>
    </ion-item>
    <ion-item v-for="(mngr, idx) in state.managers" :key="idx">
      <ion-icon class="mngr_icon" :icon="personCircleOutline"></ion-icon>
      <ion-label>
        <h6>
          {{ mngr.name }}
        </h6>
        <p class="event_mngr_email">{{ mngr.email }}</p>
        <ion-button
          class="default_manager"
          size="small"
          color="primary"
          v-if="userStore.me.id === mngr.user_account && userStore.userIsAdmin"
          >DEFAULT</ion-button
        >
      </ion-label>
      <ion-button
        fill="clear"
        v-if="userStore.me.id !== mngr.user_account && userStore.userIsAdmin"
        @click="openDialogDelete(mngr.id, mngr.user_account)"
      >
        <ion-icon
          :icon="trash"
          size="small"
          slot="end"
          color="danger"
        ></ion-icon>
      </ion-button>
      <ion-alert
        :isOpen="state.alertEventManager"
        :buttons="deleteButtons"
        header="Are you sure?"
        message="This will permanently delete this event manager."
        @willDismiss="state.alertEventManager = false"
      ></ion-alert>
    </ion-item>
    <ion-modal
      id="modal-form"
      :is-open="state.modalForm"
      @willDismiss="
        state.modalForm = false;
        state.user_form.email = '';
      "
      ref="modal"
    >
      <div>
        <h2 class="ion-margin">New Event Manager</h2>
        <ion-list lines="none">
          <ion-item>
            <ion-label position="stacked">Email</ion-label>
            <ion-input
              placeholder="john.doe@gmail.com"
              v-model="state.user_form.email"
              :disabled="state.userNeedToBeCreated"
            ></ion-input>
          </ion-item>
          <ion-item v-if="state.userNeedToBeCreated">
            <ion-label position="stacked">Name</ion-label>
            <ion-input
              placeholder="John Doe"
              v-model="state.user_form.name"
            ></ion-input>
          </ion-item>
          <ion-item v-if="state.userNeedToBeCreated">
            <ion-label position="stacked">Password</ion-label>
            <ion-input
              placeholder="••••••"
              type="password"
              v-model="state.user_form.password"
            ></ion-input>
          </ion-item>
        </ion-list>
        <div>
          <section>
            <ion-grid>
              <ion-row class="ion-margin-top action_buttons">
                <ion-col>
                  <ion-button
                    expand="block"
                    color="light"
                    @click="state.modalForm = false"
                  >
                    <ion-icon slot="start" :icon="close"></ion-icon>
                    CANCEL
                  </ion-button>
                </ion-col>
                <ion-col>
                  <ion-button
                    expand="block"
                    color="success"
                    @click="
                      state.userNeedToBeCreated ? submit() : searchForUserEmail()
                    "
                  >
                    <ion-icon slot="end" :icon="save"></ion-icon>
                    SAVE
                  </ion-button>
                </ion-col>
              </ion-row>
            </ion-grid>
          </section>
        </div>
      </div>
      <ion-loading :is-open="state.loading" message="Please wait...">
      </ion-loading>
    </ion-modal>
  </div>
</template>
<style lang="scss">
ion-item.header {
  --inner-border-width: 0 0 0 0 !important;
}
#modal-form {
  color: black;
  --max-width: 90%;
  --height: flex;
  --border-radius: 10px;
  --backdrop-opacity: var(--ion-backdrop-opacity, 0.4);
}
.action_buttons {
  margin-left: 2px;
  margin-right: 2px;
}
.alert_doesnt_exists {
  font-size: 12px;
}
.new_user_toggle {
  padding-left: 0px;
}
.default_manager {
  height: 12px;
  font-size: 10px;
  margin-top: 2px;
}
</style>
